import { Component, createContext, FormEvent, useCallback, useContext, useEffect, useId, useMemo, useReducer, useRef, useState } from "react";
import { alarmSounded, askForNotifications, buzz, cancelAlarm, cancelPush, clearRestNotification, notificationPermission, notifyRestOver, playAlarm, scheduleAlarm, schedulePush } from "./alarm";
import { pending, queue, remove, dropRetired } from "./offline";
import { ACTIVE_WORKOUT_KEY, activeWorkoutFromStorage, activeWorkoutToStorage, calendarRange, chartTicks, clampRest, clock, type ExerciseDraft, exerciseRecords, fromDrafts, groupExercises, groupLetter, isWarmup, priorForSets, recordsBroken, recordToast, renamedExercises, REST_DEFAULT, restFromStorage, restReducer, restRunning, REST_STORAGE_KEY, restToStorage, sawRestEnd, setLabels, toDrafts, today, topWorkingWeight, workingVolume } from "./helpers";

type Boot = { routines: any[]; workouts: any[] };
type Screen = "train" | "progress" | "records" | "settings";
const screens: Screen[] = ["train", "progress", "records", "settings"];
const weekdays = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const navItems: Array<{ id: Screen; label: string; icon: React.ReactNode }> = [
  { id: "train", label: "Train", icon: <><path d="M3.5 9.5v5M6.5 6.5v11M17.5 6.5v11M20.5 9.5v5M6.5 12h11" /></> },
  { id: "progress", label: "Progress", icon: <><path d="M4.5 20h15" /><path d="M7.5 20v-6M12 20V5.5M16.5 20v-9.5" /></> },
  { id: "records", label: "Records", icon: <><circle cx="12" cy="9" r="5" /><path d="M8.6 13.2 7.5 20.5l4.5-2.3 4.5 2.3-1.1-7.3" /></> },
  { id: "settings", label: "Settings", icon: <><path d="M4 7.5h7M16 7.5h4M4 12h4M13 12h7M4 16.5h7M16 16.5h4" /><circle cx="13.5" cy="7.5" r="2.2" /><circle cx="10.5" cy="12" r="2.2" /><circle cx="13.5" cy="16.5" r="2.2" /></> }
];
const day = (offset = 0) => today(offset);
const labelDay = (value: string) => new Intl.DateTimeFormat(undefined, { weekday: "short", month: "short", day: "numeric" }).format(new Date(`${value}T12:00:00`));
const number = (value: unknown) => Number(value || 0);
const compact = (value: number) => Number(value.toFixed(1));
const screenFromHash = (): Screen => { const value = location.hash.replace(/^#/, "") as Screen; return screens.includes(value) ? value : "train"; };
/** Network failures surface as "Failed to fetch", which means nothing to a person standing in a gym. */
const reason = (error: unknown, fallback: string) => !navigator.onLine ? "You’re offline — try again once you reconnect."
  : error instanceof Error && error.message && error.message !== "Failed to fetch" ? error.message : fallback;

/** The real app icon — used in the top bar and on auth/loading screens instead of "CL" initials. */
function BrandMark({ className = "logo" }: { className?: string }) {
  return <img className={className} src="/icon.svg" alt="" width={52} height={52} decoding="async" />;
}

async function api<T = any>(path: string, options: RequestInit & { queueOnFail?: boolean } = {}): Promise<T> {
  const { queueOnFail = true, ...init } = options;
  try {
    const response = await fetch(`/api/${path}`, { credentials: "same-origin", headers: { "content-type": "application/json", ...(init.headers || {}) }, ...init });
    const result = await response.json().catch(() => ({})) as { error?: string };
    if (!response.ok) throw new Error(result.error || "Request failed");
    return result as T;
  } catch (error) {
    if (queueOnFail && init.method && !["GET", "HEAD"].includes(init.method)) {
      await queue({ path, method: init.method, body: init.body ? JSON.parse(String(init.body)) : undefined });
      return { queued: true } as T;
    }
    throw error;
  }
}
async function flushOutbox() {
  if (!navigator.onLine) return 0;
  await dropRetired();
  const entries = await pending(); let completed = 0;
  for (const entry of entries) {
    try { await api(entry.path, { method: entry.method, body: entry.body ? JSON.stringify(entry.body) : undefined, queueOnFail: false }); await remove(entry.id); completed += 1; }
    catch { break; }
  }
  return completed;
}

/* ---------- toasts + confirm ---------- */

type ToastAction = { label: string; run: () => void | Promise<void> };
type ToastItem = { id: string; message: string; action?: ToastAction };
type Ui = { notify: (message: string, action?: ToastAction) => void; confirm: (message: string, label?: string) => Promise<boolean> };
const UiContext = createContext<Ui>({ notify: () => {}, confirm: async () => true });
const useUi = () => useContext(UiContext);

function UiProvider({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const [ask, setAsk] = useState<{ message: string; label: string; resolve: (value: boolean) => void } | null>(null);
  const dismiss = useCallback((id: string) => setToasts((current) => current.filter((toast) => toast.id !== id)), []);
  const notify = useCallback((message: string, action?: ToastAction) => {
    const id = crypto.randomUUID();
    setToasts((current) => [...current, { id, message, action }]);
    window.setTimeout(() => dismiss(id), 6000);
  }, [dismiss]);
  const confirm = useCallback((message: string, label = "Confirm") => new Promise<boolean>((resolve) => setAsk({ message, label, resolve })), []);
  const value = useMemo(() => ({ notify, confirm }), [notify, confirm]);
  const settle = (answer: boolean) => { ask?.resolve(answer); setAsk(null); };
  return <UiContext.Provider value={value}>{children}
    <div className="toast-layer">{toasts.map((toast) => <div className="toast" key={toast.id} role="status"><span>{toast.message}</span>{toast.action && <button onClick={() => { void toast.action?.run(); dismiss(toast.id); }}>{toast.action.label}</button>}</div>)}</div>
    {ask && <Modal title="Just checking" close={() => settle(false)}>
      <p className="subtle">{ask.message}</p>
      <div className="confirm-actions"><button className="secondary" onClick={() => settle(false)}>Cancel</button><button className="primary" onClick={() => settle(true)}>{ask.label}</button></div>
    </Modal>}
  </UiContext.Provider>;
}

/* ---------- data ---------- */

const BOOT_CACHE = "chadlite-boot-v2";

function useBoot() {
  const [boot, setBoot] = useState<Boot | null>(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const load = async () => {
    setLoading(true);
    try {
      await flushOutbox();
      const next = await api<Boot>("bootstrap", { queueOnFail: false });
      setBoot(next); localStorage.setItem(BOOT_CACHE, JSON.stringify(next)); localStorage.removeItem("rubymax-boot"); setError("");
    } catch (err) {
      const cached = localStorage.getItem(BOOT_CACHE);
      if (cached) { setBoot(JSON.parse(cached)); setError("Offline — showing your last saved data. Changes will sync when you reconnect."); }
      else setError(err instanceof Error ? err.message : "Could not load Chadlite");
    } finally { setLoading(false); }
  };
  return { boot, error, loading, load, setBoot };
}

function Auth({ onReady }: { onReady: () => void }) {
  const [needsSetup, setNeedsSetup] = useState(false); const [checking, setChecking] = useState(true);
  const [password, setPassword] = useState(""); const [setupKey, setSetupKey] = useState(""); const [error, setError] = useState("");
  useEffect(() => { api<{ authenticated: boolean; needsSetup: boolean }>("auth/status", { queueOnFail: false }).then((status) => { if (status.authenticated) onReady(); else setNeedsSetup(status.needsSetup); }).catch(() => setError("The server is unavailable. Check your connection.")).finally(() => setChecking(false)); }, [onReady]);
  const submit = async (event: FormEvent) => {
    event.preventDefault(); setError("");
    try {
      await api(needsSetup ? "auth/setup" : "auth/login", { method: "POST", body: JSON.stringify(needsSetup ? { setupKey, password } : { password }), queueOnFail: false });
      onReady();
    } catch (err) { setError(err instanceof Error ? err.message : "Could not sign in"); }
  };
  if (checking) return <main className="auth"><BrandMark className="mark" /><p>Opening Chadlite…</p></main>;
  return <main className="auth"><section className="auth-card"><BrandMark className="mark" /><p className="eyebrow">PRIVATE FITNESS LOG</p><h1>{needsSetup ? "Set up your vault" : "Welcome back"}</h1><p className="subtle">{needsSetup ? "Enter the deployment setup key once, then choose your app password." : "Your workouts stay in your private Chadlite vault."}</p>
    <form onSubmit={submit}>{needsSetup && <label>Setup key<input autoComplete="off" value={setupKey} onChange={(event) => setSetupKey(event.target.value)} required /></label>}<label>App password<input type="password" autoComplete={needsSetup ? "new-password" : "current-password"} minLength={14} value={password} onChange={(event) => setPassword(event.target.value)} required /></label>{error && <p className="error">{error}</p>}<button className="primary">{needsSetup ? "Create private vault" : "Unlock Chadlite"}</button></form>
  </section></main>;
}

/** Wipe every client-side cache. The escape hatch when the app will not start at all. */
export async function resetApp() {
  try {
    localStorage.removeItem(BOOT_CACHE);
    localStorage.removeItem("rubymax-boot");
    localStorage.removeItem(ACTIVE_WORKOUT_KEY);
    localStorage.removeItem(REST_STORAGE_KEY);
    if (typeof caches !== "undefined") await Promise.all((await caches.keys()).map((key) => caches.delete(key)));
    if (navigator.serviceWorker) await Promise.all((await navigator.serviceWorker.getRegistrations()).map((registration) => registration.unregister()));
  } catch { /* best effort — reload regardless */ }
  location.replace("/");
}

/**
 * Without this a render error unmounts the whole tree and leaves a white screen with
 * no way to find out what happened — which is exactly what it did.
 */
class ErrorBoundary extends Component<{ children: React.ReactNode }, { error: Error | null }> {
  state: { error: Error | null } = { error: null };
  static getDerivedStateFromError(error: Error) { return { error }; }
  componentDidCatch(error: Error, info: React.ErrorInfo) { console.error("Chadlite crashed", error, info); }
  render() {
    const { error } = this.state;
    if (!error) return this.props.children;
    return <main className="auth"><section className="auth-card crash-card">
      <BrandMark className="mark" />
      <h1>Something broke</h1>
      <p className="subtle">Chadlite hit an error it could not recover from. Nothing on the server was lost.</p>
      <pre className="crash">{error.message}{error.stack ? `\n\n${error.stack}` : ""}</pre>
      <button className="primary" onClick={() => location.reload()}>Reload</button>
      <button className="secondary" onClick={() => void resetApp()}>Clear cached data and restart</button>
    </section></main>;
  }
}

export default function App() {
  return <ErrorBoundary><UiProvider><Shell /></UiProvider></ErrorBoundary>;
}

function Shell() {
  const [ready, setReady] = useState(false);
  const { boot, error, load } = useBoot();
  const [screen, setScreen] = useState<Screen>(screenFromHash);
  const [pendingCount, setPendingCount] = useState(0);
  const [online, setOnline] = useState(navigator.onLine);
  const countPending = useCallback(() => { void pending().then((items) => setPendingCount(items.length)); }, []);
  const reload = async () => { await load(); countPending(); };
  useEffect(() => { if (ready) void reload(); }, [ready]);
  useEffect(() => {
    const sync = () => setScreen(screenFromHash());
    const goOnline = () => { setOnline(true); void flushOutbox().then(() => { countPending(); if (ready) void load(); }); };
    const goOffline = () => setOnline(false);
    window.addEventListener("popstate", sync); window.addEventListener("hashchange", sync);
    window.addEventListener("online", goOnline); window.addEventListener("offline", goOffline);
    countPending();
    return () => { window.removeEventListener("popstate", sync); window.removeEventListener("hashchange", sync); window.removeEventListener("online", goOnline); window.removeEventListener("offline", goOffline); };
  }, [ready, countPending]);
  const go = (next: Screen) => { setScreen(next); if (screenFromHash() !== next) location.hash = next; };
  if (!ready) return <Auth onReady={() => setReady(true)} />;
  if (!boot) return <main className="auth"><BrandMark className="mark" /><p>{error || "Loading your training log…"}</p><button onClick={() => void reload()} className="secondary">Try again</button></main>;
  const status = !online ? "offline" : pendingCount ? "syncing" : "online";
  const statusText = !online ? "Offline" : pendingCount ? `${pendingCount} waiting to sync` : "Synced";
  // Keep Training mounted across tab switches. The live workout (sets, notes, rest timer)
  // lived only in that component's useState — swapping to Records unmounted it and wiped
  // the session. Other tabs still mount on demand; only Train must survive in the background.
  return <main className="app">
    <div className="app-scroll">
      <header className="topbar"><div><BrandMark /><strong>Chadlite</strong></div><span className={status} role="status">{statusText}</span></header>
      {error && <p className="notice">{error}</p>}
      <div hidden={screen !== "train"}><Training boot={boot} reload={reload} /></div>
      {screen === "progress" ? <Progress boot={boot} /> : null}
      {screen === "records" ? <Records boot={boot} /> : null}
      {screen === "settings" ? <Settings reload={reload} logout={() => { void api("auth/logout", { method: "POST", queueOnFail: false }).finally(() => { localStorage.removeItem(ACTIVE_WORKOUT_KEY); setReady(false); }); }} /> : null}
    </div>
    <nav>{navItems.map((tab) => <button key={tab.id} className={screen === tab.id ? "active" : ""} aria-current={screen === tab.id ? "page" : undefined} onClick={() => go(tab.id)}>
      <i><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">{tab.icon}</svg></i>
      <span>{tab.label}</span>
    </button>)}</nav>
  </main>;
}

/* ---------- training ---------- */

const REST_OVER = "Time for your next set.";

function useRestTimer() {
  const [state, dispatch] = useReducer(restReducer, null, () => restFromStorage(localStorage.getItem(REST_STORAGE_KEY), Date.now()));
  const running = restRunning(state);
  const over = !running && state.remaining === 0;
  // The alert belongs to one rest, so a re-render cannot sound it twice. It starts spent when
  // the app opens onto an already-finished rest: audio needs a gesture behind it and would be
  // refused anyway, and a beep about a rest that ended before the app restarted is a puzzle.
  const alerted = useRef(over);
  // The deadline that just elapsed, which is the evidence for whether the page watched it pass
  // or woke up to find it gone. Held separately because the sync that flips `over` zeroes
  // `endsAt` in the same breath, destroying the only record of when the rest actually ended.
  const deadline = useRef(state.endsAt);
  if (state.endsAt) deadline.current = state.endsAt;

  // Deliberately not keyed on the whole state: `remaining` changes every second, and only the
  // target and the deadline are worth a write.
  useEffect(() => { localStorage.setItem(REST_STORAGE_KEY, restToStorage(state)); }, [state.target, state.endsAt]);

  useEffect(() => {
    if (!running) return;
    // Re-arm the server push whenever a rest is found running — including after reload, when
    // localStorage still has a deadline but the original schedulePush may never have landed.
    void schedulePush(state.remaining, REST_OVER);
    const sync = () => dispatch({ type: "sync", now: Date.now() });
    // Four times a second: the display is per-second, but this is also what closes the gap
    // between coming back to the app and the clock catching up.
    const interval = window.setInterval(sync, 250);
    // visibilitychange covers switching apps and locking the phone; pageshow covers a page
    // restored from the back/forward cache, which fires no visibility change at all.
    document.addEventListener("visibilitychange", sync);
    for (const event of ["pageshow", "focus"]) window.addEventListener(event, sync);
    return () => {
      clearInterval(interval);
      document.removeEventListener("visibilitychange", sync);
      for (const event of ["pageshow", "focus"]) window.removeEventListener(event, sync);
    };
  }, [running]);

  useEffect(() => {
    if (!over) { alerted.current = false; return; }
    if (alerted.current) return;
    alerted.current = true;
    // The tone queued when the rest started has already played if the audio session survived
    // the app being away. Sounding it again here is for when it did not — and only then, or
    // the everyday case chops its own tone in half a quarter of a second in.
    if (!alarmSounded()) { cancelAlarm(); playAlarm(); }
    buzz([200, 100, 200]);
    // Only when the page watched the deadline pass. Then the push is landing at this same
    // moment and shares this banner's tag, so whichever arrives first is the one seen — and
    // this is the one that works when the server could not be reached. Waking up to a rest
    // that ended while the app was away is the other case: that push already fired, and
    // posting an identical banner on re-entry is the duplicate alert.
    if (sawRestEnd(deadline.current, Date.now())) void notifyRestOver(REST_OVER);
  }, [over]);

  // The banner has done its job the moment the app is in front of you again. iOS standalone
  // PWAs can return by emitting any one of these three, so all are listened for; which one
  // arrives no longer decides anything beyond how soon the banner goes away.
  useEffect(() => {
    const clear = () => {
      if (document.visibilityState !== "visible") return;
      void clearRestNotification();
    };
    document.addEventListener("visibilitychange", clear);
    for (const event of ["pageshow", "focus"]) window.addEventListener(event, clear);
    return () => {
      document.removeEventListener("visibilitychange", clear);
      for (const event of ["pageshow", "focus"]) window.removeEventListener(event, clear);
    };
  }, []);

  /**
   * Every rest starts from a tap, which is the only time the sound can be queued: iOS will
   * not resume an audio context outside a gesture, and a rest queued from anywhere else is
   * one that rings only if the app happens to still be awake.
   */
  const startRest = (target?: number) => {
    const seconds = clampRest(target ?? state.target);
    dispatch({ type: "start", target, now: Date.now() });
    cancelAlarm();
    scheduleAlarm(seconds);
    void schedulePush(seconds, REST_OVER);
  };
  // Both of these need to know where the timer lands before dispatching, to queue the sound
  // against the new deadline, so the action is run through the reducer once here as well.
  const resume = () => {
    const action = { type: "toggle", now: Date.now() } as const;
    const next = restReducer(state, action);
    dispatch(action);
    if (restRunning(next)) scheduleAlarm(next.remaining); else cancelAlarm();
    // Pausing has to reach the server too, or a rest you stopped still buzzes you later.
    if (restRunning(next)) void schedulePush(next.remaining, REST_OVER); else void cancelPush();
  };
  const nudge = (delta: number) => {
    const action = { type: "adjust", delta, now: Date.now() } as const;
    const next = restReducer(state, action);
    dispatch(action);
    // The queued sound is tied to the old deadline, so it has to be re-queued against the new one.
    cancelAlarm({ release: false });
    if (restRunning(next)) scheduleAlarm(next.remaining);
    if (restRunning(next)) void schedulePush(next.remaining, REST_OVER); else void cancelPush();
  };
  const reset = () => { dispatch({ type: "reset" }); cancelAlarm(); void cancelPush(); };

  return { state, running, over, startRest, resume, nudge, reset };
}

/**
 * Whether WebKit's native switch control exists here, which is what decides whether a record
 * can be felt at all. `<input switch>` is a plain checkbox everywhere else, so rather than
 * lay a pointless element over the tap target on those platforms, SetCheck leaves the set row
 * exactly as it was. Should this read false on a WebKit that does support the control, the
 * cost is the missing buzz and nothing else.
 */
const switchHaptics = typeof document !== "undefined" && "switch" in document.createElement("input");

/**
 * The set-completion tick, carrying the one haptic an iPhone will give a web page.
 *
 * Safari implements no Vibration API (see alarm.ts), so a personal record cannot simply be
 * buzzed when it is detected. What WebKit does do is play the system's own tap when its
 * native switch control is toggled — and only under a real finger, the trick of firing it
 * from script having been closed off in iOS 26.5. So a switch is laid transparently over the
 * checkbox of exactly the set that would break one of the exercise's records, and takes the
 * tap: the buzz is the system's, raised by the gesture that logs the set, nothing faked.
 *
 * It is one fixed tap, with no pattern or intensity behind it, riding on a form control's
 * side effect rather than on an API — Apple can take it away in a point release, as it took
 * away the scripted version. Everything here is therefore arranged so that its absence is
 * simply no buzz: the checkbox underneath is the same control, in the same place, either way.
 */
function SetCheck({ label, done, record, onChange }: { label: string; done: boolean; record: boolean; onChange: (done: boolean) => void }) {
  // Only an uncompleted record set is worth intercepting. Un-ticking one goes through the
  // plain checkbox below, since a buzz for taking a record back reads as a second record.
  if (!record || done || !switchHaptics) return <input type="checkbox" aria-label={label} checked={done} onChange={(event) => onChange(event.target.checked)} />;
  return <>
    {/* Now a picture of a checkbox: the switch over it holds the label and takes the tap,
        so this must not be reachable by pointer, tab or screen reader as a second control. */}
    <input type="checkbox" checked={false} readOnly aria-hidden="true" tabIndex={-1} />
    {/* Set through a ref because `switch` is a bare content attribute with no React prop. */}
    <input className="haptic-switch" type="checkbox" aria-label={label} checked={false}
      ref={(node) => { node?.setAttribute("switch", ""); }}
      onChange={(event) => onChange(event.target.checked)} />
  </>;
}

function Training({ boot, reload }: { boot: Boot; reload: () => Promise<void> }) {
  const { confirm, notify } = useUi();
  // Rehydrate a mid-session draft if the page was discarded (or the tab remounts). Kept in
  // sync below whenever the live workout changes; cleared on discard / finish.
  const restored = useRef(activeWorkoutFromStorage(localStorage.getItem(ACTIVE_WORKOUT_KEY)));
  const [active, setActive] = useState<any>(() => restored.current?.workout ?? null);
  const [notes, setNotes] = useState(() => restored.current?.notes ?? "");
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState<any>(null);
  // Routines are built and edited here, on the screen that already lists them. A separate
  // library tab showed the same routines a second time to hold these two buttons.
  const [editing, setEditing] = useState<{ item?: any } | null>(null);
  const rest = useRestTimer();
  const workoutId = useRef(restored.current?.id ?? "");
  useEffect(() => {
    if (!active) { localStorage.removeItem(ACTIVE_WORKOUT_KEY); return; }
    if (!workoutId.current) workoutId.current = crypto.randomUUID();
    localStorage.setItem(ACTIVE_WORKOUT_KEY, activeWorkoutToStorage({ id: workoutId.current, notes, workout: active }));
  }, [active, notes]);
  /** Every exercise's standing records, so a set being typed can be measured against them. */
  const records = useMemo(() => exerciseRecords(boot.workouts), [boot.workouts]);
  const previous = (name: string) => boot.workouts.flatMap((workout) => workout.exercises || []).find((exercise) => String(exercise.name).toLowerCase() === name.toLowerCase());
  const lastPerformed = (routine: any) => boot.workouts.find((workout) => workout.routine_id === routine.id || workout.routine_name === routine.name)?.performed_on;
  // Bootstrap orders workouts newest first (and includes created_at as the same-day tiebreaker),
  // so this is the one routine to call out — not every routine that has ever been trained.
  const mostRecentWorkout = boot.workouts[0];
  const isMostRecentlyCompleted = (routine: any) => Boolean(mostRecentWorkout && (mostRecentWorkout.routine_id === routine.id || mostRecentWorkout.routine_name === routine.name));
  const start = (routine: any) => {
    workoutId.current = crypto.randomUUID();
    setActive({ ...routine, exercises: routine.exercises.map((exercise: any) => {
      // Carry last time's sets alongside, so the table can show them and pre-fill the weight.
      const priorSets: any[] = previous(String(exercise.name))?.sets || [];
      const blank = [
        ...Array.from({ length: Math.max(0, number(exercise.warmup_sets)) }, () => ({ set_type: "warmup" })),
        ...Array.from({ length: Math.max(1, number(exercise.default_sets) || 3) }, () => ({ set_type: "working" }))
      ];
      const matched = priorForSets(priorSets, blank);
      return {
        ...exercise,
        restSeconds: clampRest(number(exercise.rest_seconds) || REST_DEFAULT),
        exerciseNotes: "",
        sets: blank.map((set, index) => ({
          set_type: set.set_type,
          weight: matched[index] ? String(compact(number(matched[index].weight))) : "",
          reps: "",
          completed: false
        })),
        previousSets: priorSets
      };
    }) });
    setNotes(""); setPreview(null); rest.reset();
  };
  const patchExercise = (exerciseIndex: number, change: (exercise: any) => any) => setActive((workout: any) => ({ ...workout, exercises: workout.exercises.map((exercise: any, index: number) => index === exerciseIndex ? change(exercise) : exercise) }));
  const changeSet = (exerciseIndex: number, setIndex: number, field: string, value: string | boolean) => patchExercise(exerciseIndex, (exercise) => ({ ...exercise, sets: exercise.sets.map((set: any, position: number) => position !== setIndex ? set : { ...set, [field]: value }) }));
  const applyPrevious = (exerciseIndex: number, setIndex: number, prior: any) => patchExercise(exerciseIndex, (exercise) => ({
    ...exercise, sets: exercise.sets.map((set: any, position: number) => position !== setIndex ? set : { ...set, weight: String(compact(number(prior.weight))), reps: String(compact(number(prior.reps))) })
  }));
  const adjustExerciseRest = (exerciseIndex: number, delta: number) => patchExercise(exerciseIndex, (exercise) => ({ ...exercise, restSeconds: clampRest(exercise.restSeconds + delta) }));
  const toggleSetType = (exerciseIndex: number, setIndex: number) => changeSet(exerciseIndex, setIndex, "set_type", isWarmup(active?.exercises[exerciseIndex]?.sets[setIndex]) ? "working" : "warmup");
  const addSet = (exerciseIndex: number, type: "warmup" | "working") => patchExercise(exerciseIndex, (exercise) => {
    const row = { set_type: type, weight: exercise.sets.filter((set: any) => isWarmup(set) === (type === "warmup")).at(-1)?.weight || "", reps: "", completed: false };
    // Warm-ups belong above the working sets, so insert rather than append.
    const at = type === "warmup" ? exercise.sets.filter((set: any) => isWarmup(set)).length : exercise.sets.length;
    return { ...exercise, sets: [...exercise.sets.slice(0, at), row, ...exercise.sets.slice(at)] };
  });
  const completeSet = (exerciseIndex: number, setIndex: number, done: boolean) => {
    if (done && active) {
      const exercise = active.exercises[exerciseIndex];
      const standing = records.get(String(exercise.name).trim().toLowerCase());
      const broken = recordsBroken(standing, exercise.sets, setIndex);
      if (broken.length) {
        notify(recordToast(broken));
        // iPhone already buzzes via the native switch overlay; everywhere else, vibrate if we can.
        if (!switchHaptics) buzz([50, 40, 50]);
      }
    }
    changeSet(exerciseIndex, setIndex, "completed", done);
    if (done) rest.startRest(active?.exercises[exerciseIndex]?.restSeconds);
  };
  const discard = async () => { if (await confirm("Discard this workout? Nothing will be saved.", "Discard")) { setActive(null); rest.reset(); } };
  const removeRoutine = async (routine: any) => {
    if (!await confirm(`Delete “${routine.name}”? This can’t be undone.`, "Delete")) return;
    try { await api(`routines/${routine.id}`, { method: "DELETE", queueOnFail: false }); setPreview(null); notify(`Deleted ${routine.name}`); await reload(); }
    catch (error) { notify(reason(error, "Could not delete that routine")); }
  };
  const finish = async () => {
    if (!active || saving) return;
    setSaving(true);
    try {
      await api("workouts", {
        method: "POST",
        body: JSON.stringify({
          id: workoutId.current,
          routineId: active.id,
          routineName: active.name,
          performedOn: day(),
          notes,
          exercises: active.exercises.map((exercise: any) => ({
            name: exercise.name,
            notes: exercise.exerciseNotes || exercise.notes,
            superset_group: number(exercise.superset_group),
            sets: exercise.sets.map((set: any) => ({ weight: number(set.weight), reps: number(set.reps), completed: Boolean(set.completed), set_type: set.set_type }))
          }))
        })
      });
      setActive(null); rest.reset(); await reload();
    } finally { setSaving(false); }
  };
  if (active) {
    return <section className="page workout">
      <div className="page-head"><div><p className="eyebrow">ACTIVE WORKOUT</p><h1>{active.name}</h1></div><button className="text-button" onClick={() => void discard()}>Discard</button></div>
      {/* Sticky: a rest timer you have to scroll back to the top to read is a rest timer you
          cannot use between two sets that are halfway down the workout. */}
      <section className={`timer${rest.running ? " active-timer" : ""}${rest.over ? " elapsed" : ""}`}>
        {/* aria-live so the countdown reaching zero is announced, not only shown and sounded. */}
        <div className="timer-display"><b>{clock(rest.state.remaining)}</b><span aria-live="polite">{rest.over ? "rest over" : `${clock(rest.state.target)} target`}</span></div>
        <div className="timer-adjust"><button aria-label="Shorten rest by 15 seconds" onClick={() => rest.nudge(-15)}>−15</button><button aria-label="Lengthen rest by 15 seconds" onClick={() => rest.nudge(15)}>+15</button></div>
        <button className="secondary" onClick={rest.resume}>{rest.running ? "Pause" : "Start"}</button>
        <button className="text-button" onClick={rest.reset}>Reset</button>
      </section>
      {groupExercises(active.exercises.map((exercise: any, exerciseIndex: number) => ({ ...exercise, exerciseIndex }))).map((block, blockIndex) => {
        const cards = block.items.map((exercise: any) => {
          const exerciseIndex = exercise.exerciseIndex;
          const target = String(exercise.target_reps || "");
          const labels = setLabels(exercise.sets);
          const priors = priorForSets<any>(exercise.previousSets || [], exercise.sets);
          const standing = records.get(String(exercise.name).trim().toLowerCase());
          return <section className="exercise-card" key={exercise.id ?? exerciseIndex}>
            <h2>{exercise.name}</h2>
            <input className="exercise-notes" placeholder="Add notes here…" aria-label={`Notes for ${exercise.name}`} value={exercise.exerciseNotes} onChange={(event) => patchExercise(exerciseIndex, (current) => ({ ...current, exerciseNotes: event.target.value }))} />
            <div className="rest-row">
              <span>⏱ Rest {clock(exercise.restSeconds)}</span>
              <button type="button" aria-label={`Shorten rest for ${exercise.name}`} onClick={() => adjustExerciseRest(exerciseIndex, -15)}>−15</button>
              <button type="button" aria-label={`Lengthen rest for ${exercise.name}`} onClick={() => adjustExerciseRest(exerciseIndex, 15)}>+15</button>
            </div>
            <div className="set-table">
              <span>Set</span><span>Previous</span><span>lb</span><span>Reps</span><span aria-label="Completed">✓</span>
              {exercise.sets.map((set: any, setIndex: number) => {
                const prior = priors[setIndex];
                const warm = isWarmup(set);
                const label = labels[setIndex];
                const what = `${exercise.name} ${warm ? "warm-up" : `set ${label}`}`;
                const broken = recordsBroken(standing, exercise.sets, setIndex);
                const isRecord = broken.length > 0;
                return <div className={`set-row${set.completed ? " done" : ""}${warm ? " warm" : ""}${isRecord ? " record" : ""}`} key={setIndex}>
                  <button type="button" className={`set-num${warm ? " warm" : ""}${isRecord ? " record" : ""}`} aria-label={`${what}${isRecord ? `, new ${broken.join(" and ")} record` : ""}. Tap to make it a ${warm ? "working" : "warm-up"} set.`} onClick={() => toggleSetType(exerciseIndex, setIndex)}>
                    <span>{label}</span>
                    {isRecord && <span className="pr-tag" aria-hidden="true">PR</span>}
                  </button>
                  <button type="button" className="prev-cell" disabled={!prior}
                    aria-label={prior ? `Copy previous ${compact(number(prior.weight))} pounds by ${compact(number(prior.reps))} reps into ${what}` : `No previous ${what}`}
                    onClick={() => prior && applyPrevious(exerciseIndex, setIndex, prior)}>
                    {prior ? `${compact(number(prior.weight))}lb × ${compact(number(prior.reps))}` : "—"}
                  </button>
                  <input inputMode="decimal" aria-label={`${what} weight in pounds`} value={set.weight} onChange={(event) => changeSet(exerciseIndex, setIndex, "weight", event.target.value)} />
                  <input inputMode="decimal" placeholder={warm ? "reps" : target || "reps"} aria-label={`${what} reps${!warm && target ? `, target ${target}` : ""}`} value={set.reps} onChange={(event) => changeSet(exerciseIndex, setIndex, "reps", event.target.value)} />
                  <span className={`set-done${isRecord ? " record" : ""}`}><SetCheck label={`Mark ${what} complete`} done={Boolean(set.completed)} record={isRecord}
                    onChange={(done) => completeSet(exerciseIndex, setIndex, done)} /></span>
                </div>;
              })}
            </div>
            <div className="add-row">
              <button type="button" className="add-warm" onClick={() => addSet(exerciseIndex, "warmup")}>+ Warm-up</button>
              <button type="button" className="add-set" onClick={() => addSet(exerciseIndex, "working")}>+ Add Set</button>
            </div>
          </section>;
        });
        if (!block.group) return <div key={blockIndex}>{cards}</div>;
        return <div className="superset" key={blockIndex}>
          <p className="superset-label">Superset {groupLetter(block.group)} · {block.items.length} exercises back to back</p>
          {cards}
        </div>;
      })}
      <label>Workout notes<textarea value={notes} onChange={(event) => setNotes(event.target.value)} placeholder="How did it feel?" /></label>
      <button className="primary wide" disabled={saving} onClick={() => void finish()}>{saving ? "Saving…" : "Finish workout"}</button>
    </section>;
  }
  return <section className="page"><div className="page-head"><div><p className="eyebrow">TRAINING</p><h1>Choose a routine</h1></div>
      <div className="head-actions"><button className="primary small" onClick={() => setEditing({})}>+ New</button></div></div>
    {boot.routines.map((routine) => {
      const last = lastPerformed(routine);
      const newest = isMostRecentlyCompleted(routine);
      return <button className={`routine${newest ? " most-recent" : ""}`} key={routine.id} onClick={() => setPreview(routine)}>
        <strong>{routine.name}</strong>
        <span>{routine.exercises.length} exercises{last ? ` · ${newest ? "last completed" : "last"} ${labelDay(last)}` : " · not yet trained"}</span>
        <i aria-hidden="true">›</i>
      </button>;
    })}
    {!boot.routines.length && <Empty title="Your gym is ready" detail="Tap + New to build your first routine. Its sets and last results then appear here, ready to train." />}
    {preview && <Modal title={preview.name} close={() => setPreview(null)}>
      <p className="subtle">{preview.exercises.length} exercises{lastPerformed(preview) ? ` · last trained ${labelDay(lastPerformed(preview))}` : " · not trained yet"}</p>
      <div className="preview-list">{groupExercises(preview.exercises).map((block, blockIndex) => <div className={block.group ? "preview-block superset" : "preview-block"} key={blockIndex}>
        {block.group > 0 && <p className="superset-label">Superset {groupLetter(block.group)}</p>}
        {block.items.map((exercise: any) => {
          const prior = previous(String(exercise.name));
          const warmups = number(exercise.warmup_sets);
          return <div className="preview-row" key={exercise.id}>
            <div><strong>{exercise.name}</strong>{prior && <small>Last: {(prior.sets || []).map((set: any) => `${isWarmup(set) ? "W " : ""}${compact(number(set.weight))}×${compact(number(set.reps))}`).join(" · ")}</small>}</div>
            <span>{warmups > 0 ? `${warmups}W + ` : ""}{number(exercise.default_sets) || 3} × {exercise.target_reps || "—"}<small>{clock(clampRest(number(exercise.rest_seconds) || REST_DEFAULT))} rest</small></span>
          </div>;
        })}
      </div>)}</div>
      {!preview.exercises.length && <p className="empty">This routine has no exercises yet. Edit it to add some.</p>}
      {/* Editing lives behind the routine rather than beside it in the list: that list is for
          starting a workout, and a delete within thumb's reach of that is a bad trade. Stacked
          rather than paired, so the action you came for stays the widest and the one you
          cannot undo is the smallest and last. */}
      <button className="primary" disabled={!preview.exercises.length} onClick={() => start(preview)}>Start workout</button>
      <button className="secondary" onClick={() => { setEditing({ item: preview }); setPreview(null); }}>Edit routine</button>
      <button className="text-button remove" onClick={() => void removeRoutine(preview)}>Delete routine</button>
    </Modal>}
    {editing && <RoutineForm item={editing.item} workouts={boot.workouts} close={() => setEditing(null)} saved={async () => { setEditing(null); await reload(); }} />}
  </section>;
}

/* ---------- progress ---------- */

function Progress({ boot }: { boot: Boot }) {
  const [selected, setSelected] = useState("");
  const [detail, setDetail] = useState<string | null>(null);
  const exercises = [...new Set(boot.workouts.flatMap((workout) => (workout.exercises || []).map((exercise: any) => exercise.name)))] as string[];
  useEffect(() => { if (!selected && exercises[0]) setSelected(exercises[0]); }, [selected, exercises.join(",")]);
  // Warm-ups are excluded so a heavy warm-up day does not read as progress.
  const data = boot.workouts.slice().reverse().flatMap((workout) => (workout.exercises || []).filter((exercise: any) => exercise.name === selected).map((exercise: any) => ({ date: workout.performed_on, total: workingVolume(exercise.sets || []), top: topWorkingWeight(exercise.sets || []) })));
  const now = day();
  const shown = data.slice(-12);
  const max = Math.max(1, ...shown.map((point) => point.total));
  const latest = shown.at(-1) ?? { date: now, total: 0, top: 0 };
  const delta = shown.length > 1 ? latest.total - shown[shown.length - 2].total : null;
  const ticks = useMemo(() => chartTicks(shown.map((point) => point.date)), [shown.map((point) => point.date).join(",")]);
  const dates = new Set(boot.workouts.map((workout) => workout.performed_on));
  const cells = useMemo(() => calendarRange(now), [now]);
  return <section className="page"><div className="page-head"><div><p className="eyebrow">PROGRESS</p><h1>Train, then review</h1></div></div>
    <section className="calendar">
      <div className="calendar-title"><h2>Workout calendar</h2><span>{boot.workouts.length} logged</span></div>
      <div className="calendar-head" aria-hidden="true">{weekdays.map((name, index) => <span key={index}>{name.slice(0, 1)}</span>)}</div>
      <div className="calendar-grid">{cells.map((value) => {
        const trained = dates.has(value);
        // Only trained days are actionable — an empty square has no workout to open.
        return <button key={value} disabled={value > now || !trained} className={`${trained ? "trained" : ""}${value === now ? " today" : ""}`.trim()}
          aria-label={`${labelDay(value)}${trained ? " — trained, view workout" : " — no workout"}`} onClick={() => setDetail(value)}><span aria-hidden="true">{Number(value.slice(8))}</span></button>;
      })}</div>
      <p className="calendar-legend"><i className="trained-key" aria-hidden="true" /> Tap a highlighted day to see that workout.</p>
    </section>
    <section className="chart-card">
      <label>Exercise<select value={selected} onChange={(event) => setSelected(event.target.value)}>{exercises.map((exercise) => <option key={exercise}>{exercise}</option>)}</select></label>
      {shown.length ? <>
        <div className="chart-head">
          <div><b>{Math.round(latest.total).toLocaleString()} lb</b><span>total volume · {labelDay(latest.date)}</span></div>
          {delta !== null && <span className={delta >= 0 ? "delta up" : "delta down"}>{delta >= 0 ? "+" : "−"}{Math.abs(Math.round(delta)).toLocaleString()}</span>}
        </div>
        {/* Bar height is volume; labelling each bar with top weight encoded two different
            metrics in one chart, and the labels collided. Value now lives in the header. */}
        <div className="bars" aria-hidden="true">{shown.map((point, index) => <div className={index === shown.length - 1 ? "bar latest" : "bar"} key={index}>
          <i style={{ height: `${Math.max(4, point.total / max * 100)}%` }} />
          <span>{ticks[index]}</span>
        </div>)}</div>
        <p className="chart-note">Peak {Math.round(max).toLocaleString()} lb · top set {Math.round(latest.top)} lb · warm-ups excluded</p>
        <table className="visually-hidden"><caption>{selected} history</caption>
          <thead><tr><th scope="col">Date</th><th scope="col">Total volume (lb)</th><th scope="col">Top working set (lb)</th></tr></thead>
          <tbody>{shown.map((point, index) => <tr key={index}><td>{labelDay(point.date)}</td><td>{Math.round(point.total)}</td><td>{Math.round(point.top)}</td></tr>)}</tbody>
        </table>
      </> : <Empty title="No exercise history yet" detail="Complete a workout to see total-volume progression." />}
    </section>
    <section className="history"><h2>Recent workouts</h2>{boot.workouts.slice(0, 12).map((workout) => <button key={workout.id} onClick={() => setDetail(workout.performed_on)}><strong>{workout.routine_name}</strong><span>{labelDay(workout.performed_on)} · {workout.exercises?.length || 0} exercises</span></button>)}</section>
    {detail && <WorkoutDetail date={detail} workouts={boot.workouts.filter((workout) => workout.performed_on === detail)} close={() => setDetail(null)} />}
  </section>;
}

function WorkoutDetail({ date, workouts, close }: { date: string; workouts: any[]; close: () => void }) {
  return <Modal title={labelDay(date)} close={close}>
    {workouts.map((workout) => {
      const volume = workingVolume((workout.exercises || []).flatMap((exercise: any) => exercise.sets || []));
      return <section className="detail-workout" key={workout.id}>
        <div className="detail-head"><h3>{workout.routine_name}</h3><span>{Math.round(volume).toLocaleString()} lb volume</span></div>
        {groupExercises(workout.exercises || []).map((block, blockIndex) => <div className={block.group ? "detail-block superset" : "detail-block"} key={blockIndex}>
          {block.group > 0 && <p className="superset-label">Superset {groupLetter(block.group)}</p>}
          {block.items.map((exercise: any) => <div className="detail-exercise" key={exercise.id}>
            <strong>{exercise.name}</strong>
            <p>{(exercise.sets || []).map((set: any, index: number) => <span key={index} className={`${number(set.completed) ? "" : "skipped"}${isWarmup(set) ? " warm" : ""}`.trim()}>{isWarmup(set) ? "W " : ""}{compact(number(set.weight))} × {compact(number(set.reps))}</span>)}</p>
          </div>)}
        </div>)}
        {workout.notes && <p className="subtle detail-notes">{workout.notes}</p>}
      </section>;
    })}
  </Modal>;
}

/* ---------- records ---------- */

const pounds = (value: number) => `${Math.round(value).toLocaleString()} lb`;

/**
 * Every exercise's personal bests — the same records the set tick buzzes for, laid out.
 *
 * Ordered by whichever record moved most recently, so the screen opens on what you have just
 * done rather than on whatever is alphabetically first. Exercises are only here once they
 * have history; a routine you have not trained yet has no bests to show.
 */
function Records({ boot }: { boot: Boot }) {
  const records = useMemo(() => exerciseRecords(boot.workouts), [boot.workouts]);
  // The name as last written, since the records are keyed by a lower-cased one.
  const names = useMemo(() => {
    const seen = new Map<string, string>();
    for (const workout of boot.workouts) {
      for (const exercise of workout.exercises || []) {
        const name = String(exercise.name ?? "").trim();
        if (name) seen.set(name.toLowerCase(), name);
      }
    }
    return seen;
  }, [boot.workouts]);

  const listed = useMemo(() => [...records.entries()]
    .map(([key, held]) => ({ key, name: names.get(key) ?? key, held, latest: [held.best?.on, held.setVolume.on, held.sessionVolume.on].filter(Boolean).sort().at(-1) ?? "" }))
    .sort((a, b) => b.latest.localeCompare(a.latest) || a.name.localeCompare(b.name)), [records, names]);

  return <section className="page"><div className="page-head"><div><p className="eyebrow">RECORDS</p><h1>Your personal bests</h1></div></div>
    {listed.map(({ key, name, held }) => <section className="record-card" key={key}>
      <h2>{name}</h2>
      {held.best && <div className="record-row">
        <div><strong>Heaviest set</strong><small>{held.best.reps} {held.best.reps === 1 ? "rep" : "reps"} at that weight</small></div>
        <span>{pounds(held.best.weight)}{held.best.on && <small>{labelDay(held.best.on)}</small>}</span>
      </div>}
      <div className="record-row">
        <div><strong>Best set</strong><small>weight × reps in one set</small></div>
        <span>{pounds(held.setVolume.volume)}{held.setVolume.on && <small>{labelDay(held.setVolume.on)}</small>}</span>
      </div>
      <div className="record-row">
        <div><strong>Best session</strong><small>total volume in one workout</small></div>
        <span>{pounds(held.sessionVolume.volume)}{held.sessionVolume.on && <small>{labelDay(held.sessionVolume.on)}</small>}</span>
      </div>
    </section>)}
    {!listed.length && <Empty title="No records yet" detail="Finish a workout and every exercise in it gets a card here — heaviest set, best single set, and best session." />}
    {listed.length > 0 && <p className="subtle">Warm-ups and sets you left unticked are not counted. During a workout, a set that would beat one of these shows PR on the row — tick it and you get a toast for each record it breaks.</p>}
  </section>;
}

function RoutineForm({ item, workouts = [], close, saved }: { item?: any; workouts?: any[]; close: () => void; saved: (id?: string) => Promise<void> }) {
  const editing = Boolean(item?.id);
  const [name, setName] = useState(String(item?.name || ""));
  const [drafts, setDrafts] = useState<ExerciseDraft[]>(() => toDrafts(item?.exercises));
  const [busy, setBusy] = useState(false); const [error, setError] = useState("");
  const patchDraft = (index: number, change: Partial<ExerciseDraft>) => setDrafts((current) => current.map((draft, position) => position === index ? { ...draft, ...change } : draft));
  const originalNames = useMemo(() => (item?.exercises || []).map((exercise: any) => String(exercise.name)), [item]);
  const loggedSets = (exerciseName: string) => workouts.flatMap((workout) => workout.exercises || [])
    .filter((exercise: any) => String(exercise.name).toLowerCase() === exerciseName.toLowerCase())
    .reduce((sum: number, exercise: any) => sum + (exercise.sets?.length || 0), 0);
  const warnings = editing
    ? renamedExercises(originalNames, drafts.map((draft) => draft.name.trim())).map((pair) => ({ ...pair, count: loggedSets(pair.from) })).filter((pair) => pair.count > 0)
    : [];
  const submit = async (event: FormEvent) => {
    event.preventDefault(); if (busy) return; setBusy(true); setError("");
    try {
      let id = item?.id as string | undefined;
      await api(editing ? `routines/${item.id}` : "routines", { method: editing ? "PUT" : "POST", body: JSON.stringify({ name, exercises: fromDrafts(drafts) }), queueOnFail: false }).then((result) => { id = result.id || id; });
      await saved(id);
    } catch (err) { setError(reason(err, "Could not save")); setBusy(false); }
  };
  return <Modal title={`${editing ? "Edit" : "New"} routine`} close={close}>
    <form onSubmit={(event) => void submit(event)}>
      <label>Name<input autoFocus value={name} onChange={(event) => setName(event.target.value)} required /></label>
      <h3 className="builder-title">Exercises</h3>
      {drafts.map((draft, index) => <div className={draft.group > 0 ? "draft-card grouped" : "draft-card"} key={draft.key}>
        <div className="draft-top">
          <input aria-label={`Exercise ${index + 1} name`} placeholder="Back squat" value={draft.name} onChange={(event) => patchDraft(index, { name: event.target.value })} />
          <button type="button" aria-label={`Remove exercise ${index + 1}`} onClick={() => setDrafts((current) => current.filter((_, position) => position !== index))}>×</button>
        </div>
        <div className="draft-fields">
          <label>Sets<input type="number" min="1" value={draft.sets} onChange={(event) => patchDraft(index, { sets: Math.max(1, Math.round(number(event.target.value))) })} /></label>
          <label>Warm-up<input type="number" min="0" max="10" value={draft.warmups} onChange={(event) => patchDraft(index, { warmups: Math.max(0, Math.round(number(event.target.value))) })} /></label>
          <label>Reps<input placeholder="8-12" value={draft.reps} onChange={(event) => patchDraft(index, { reps: event.target.value })} /></label>
          <label>Rest s<input type="number" min="15" step="15" value={draft.rest} onChange={(event) => patchDraft(index, { rest: number(event.target.value) })} /></label>
          <label>Superset<select value={draft.group} onChange={(event) => patchDraft(index, { group: number(event.target.value) })}>
            <option value={0}>—</option>
            {[1, 2, 3, 4].map((group) => <option key={group} value={group}>{groupLetter(group)}</option>)}
          </select></label>
        </div>
      </div>)}
      {!drafts.length && <p className="empty">No exercises yet.</p>}
      <p className="subtle">Give neighbouring exercises the same superset letter to perform them back to back.</p>
      <button type="button" className="text-button" onClick={() => setDrafts((current) => [...current, { key: crypto.randomUUID(), name: "", sets: 3, warmups: 0, reps: "8-12", rest: REST_DEFAULT, group: 0 }])}>+ Add exercise</button>
      {warnings.length > 0 && <div className="warn-box"><strong>Renaming hides past results</strong>
        {warnings.map((pair) => <div key={pair.from}>“{pair.from}” → “{pair.to}”: {pair.count} logged {pair.count === 1 ? "set" : "sets"} will no longer show as previous results.</div>)}</div>}
      {error && <p className="error">{error}</p>}
      <button type="submit" className="primary" disabled={busy}>{busy ? "Saving…" : "Save routine"}</button>
    </form>
  </Modal>;
}

/* ---------- settings ---------- */

function RestAlerts() {
  const [permission, setPermission] = useState(notificationPermission);
  const ask = async () => setPermission(await askForNotifications());
  return <section className="settings-card"><h2>Rest timer alerts</h2>
    <p>The countdown runs off the clock, so it keeps time while you are in another app or the
      screen is off. It sounds a tone when the rest ends; the tone is queued when the rest
      starts, so it plays even if this app has been put to sleep by then.</p>
    {permission === "granted"
      ? <p className="subtle">Notifications are on. The end of a rest is pushed from the server at the deadline, so the
          banner and the phone’s own sound and buzz arrive on time even while you are in another app or the screen is
          off. It needs a connection at that moment; without one you still get the tone.</p>
      : permission === "denied"
        ? <p className="subtle">Notifications are blocked. Turn them back on for this app in the phone’s Settings if you want a banner as well as the tone.</p>
        : permission === "unsupported"
          ? <p className="subtle">This browser cannot post notifications, so the tone is the only alert.</p>
          : <>
            <p className="subtle">A notification adds the phone’s own sound and buzz when a rest ends, and is the only
              alert that arrives on time while you are in another app. On an iPhone this needs the app added to the
              Home Screen and opened from there — Safari tabs cannot be granted it.</p>
            <button onClick={() => void ask()}>Allow notifications</button>
          </>}
    <p className="subtle">Turning your ringer’s silent switch on silences the tone, as it does for any web page.
      Nothing here can put the timer on the Lock Screen or in the Dynamic Island: those are Live Activities,
      which only a native app can draw.</p>
  </section>;
}

function Settings({ reload, logout }: { reload: () => Promise<void>; logout: () => void }) {
  const { confirm } = useUi();
  const [preview, setPreview] = useState<any>(null);
  const [importCsv, setImportCsv] = useState(""); const [importing, setImporting] = useState(false);
  const upload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]; if (!file) return;
    const csv = await file.text(); setImportCsv(csv);
    setPreview(await api("imports/preview", { method: "POST", body: JSON.stringify({ source: "hevy", csv }), queueOnFail: false }));
  };
  const commitImport = async () => {
    if (!preview?.valid || !importCsv) return;
    if (!await confirm(`Import ${preview.rowCount} rows? Imported history is added to your existing data.`, "Import")) return;
    setImporting(true);
    try { const result = await api<any>("imports/commit", { method: "POST", body: JSON.stringify({ source: "hevy", csv: importCsv }), queueOnFail: false }); setPreview({ ...preview, committed: result }); await reload(); }
    finally { setImporting(false); }
  };
  const columns: string[] = (preview?.headers || []).slice(0, 8);
  return <section className="page"><div className="page-head"><div><p className="eyebrow">SETTINGS</p><h1>Your data, yours</h1></div></div>
    <section className="settings-card"><h2>Backup</h2>
      <a href="/api/export/json">Download full JSON backup</a>
    </section>
    <RestAlerts />
    <section className="settings-card"><h2>Import history</h2>
      <p>Review a Hevy workouts CSV before adding it. Imported workouts and sets are appended to your existing training history.</p>
      <input type="file" accept=".csv,text/csv" aria-label="Choose a Hevy CSV file" onChange={(event) => void upload(event)} />
      {preview && <div className="import-summary">
        <div className="chips">
          <span className={preview.valid ? "chip good" : "chip bad"}>{preview.valid ? "Layout recognised" : "Layout not recognised"}</span>
          <span className="chip">{preview.rowCount} rows</span>
          <span className="chip">{preview.headers.length} columns</span>
          {(preview.missing || []).map((column: string) => <span className="chip bad" key={column}>Missing “{column}”</span>)}
        </div>
        {columns.length > 0 && <div className="sample-scroll"><table className="sample-table">
          <caption className="visually-hidden">First rows of the uploaded file</caption>
          <thead><tr>{columns.map((column) => <th scope="col" key={column}>{column}</th>)}</tr></thead>
          <tbody>{(preview.sample || []).map((row: Record<string, string>, index: number) => <tr key={index}>{columns.map((column) => <td key={column}>{row[column] || "—"}</td>)}</tr>)}</tbody>
        </table>{preview.headers.length > columns.length && <p className="subtle">Showing the first {columns.length} of {preview.headers.length} columns.</p>}</div>}
        {preview.valid && !preview.committed && <button className="primary" disabled={importing} onClick={() => void commitImport()}>{importing ? "Importing…" : `Import ${preview.rowCount} rows`}</button>}
        {preview.committed && <p className="subtle">{preview.committed.report}</p>}
      </div>}
    </section>
    <button className="secondary wide" onClick={logout}>Sign out</button>
  </section>;
}

/* ---------- primitives ---------- */

function Modal({ title, close, children }: { title: string; close: () => void; children: React.ReactNode }) {
  const ref = useRef<HTMLDialogElement>(null);
  const titleId = useId();
  // showModal() supplies the focus trap, focus restore, Escape handling and top-layer stacking.
  useEffect(() => { const node = ref.current; if (node && !node.open) node.showModal(); return () => { if (node?.open) node.close(); }; }, []);
  return <dialog className="modal" ref={ref} aria-labelledby={titleId}
    onCancel={(event) => { event.preventDefault(); close(); }}
    onClick={(event) => { if (event.target === ref.current) close(); }}>
    <section className="sheet">
      <header><h2 id={titleId}>{title}</h2><button type="button" aria-label="Close" onClick={close}>×</button></header>
      {children}
    </section>
  </dialog>;
}

function Empty({ title, detail }: { title: string; detail: string }) { return <div className="empty-card"><strong>{title}</strong><p>{detail}</p></div>; }
